import { KeywordContext } from "@app/types/Page";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import { getUrlPathFromAbsoluteUrl } from "@pages/Article/utils/helpers";
import Description from "@pages/Keyword/templates/TemplateWithHighlights/components/Description";
import MoreStories from "@pages/Keyword/templates/TemplateWithHighlights/components/MoreStories";
import TopStories from "@pages/Keyword/templates/TemplateWithHighlights/components/TopStories";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { sectionNavigationItems } from "@util/constant";
import { useDetectAdBlock } from "adblock-detect-react";

type TemplateWithHighlightsProps = Pick<KeywordContext, "data">;

/**
 * Template for Keyword pages withHighlighted.
 * Figma: https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=2096-14872&t=m7103yrVxON0jRlB-4
 */
const TemplateWithHighlights = ({
  data: { title, entity, highlightArticles, overview },
}: TemplateWithHighlightsProps): React.ReactElement => {
  const {
    tag: { name, urlPath },
  } = entity;

  const adBlockDetected = useDetectAdBlock();
  const relativePath = getUrlPathFromAbsoluteUrl(urlPath);
  return (
    <div className="w-full" data-testid="with-highlight-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path={relativePath}
        adTargets={[
          { key: "page", value: "listing" },
          { key: "bttags", value: getFormattedTextForAds(name) },
        ]}
      />

      <GAData
        chapter1={name}
        level2={"keywords"}
        title={"keywords"}
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${name} - Find ${name} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="article"
        slug={relativePath}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        parentCategory="keywords"
        childCategory={relativePath.replace("/keywords/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <Container rootClassName="pt-6">
        <Description className="mb-3" tag={entity} />

        <TopStories
          cardProps={highlightArticles || []}
          className="mb-16 mt-8 lg:mt-0 lg:border-t lg:border-gray-850 lg:pt-8"
        />

        <>
          {highlightArticles &&
          highlightArticles.length > 0 &&
          overview.length > 0 ? (
            <BlockTitle
              text="MORE STORIES"
              rootClassName="pt-2 border-t border-gray-850"
            />
          ) : null}
        </>

        <MoreStories cardProps={overview} className="mb-16 mt-8" />
      </Container>

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default TemplateWithHighlights;
